import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Search,
  Globe,
  Zap,
  Heart,
  MapPin,
  Sparkles,
  ArrowRight,
  Check,
  ChevronDown,
} from "lucide-react";
import { useT } from "@/lib/i18n";
import { CALCULATORS, getCalculator, type Calculator } from "@/lib/calculators";
import { Icon } from "@/components/Icon";
import { buildSeo, SITE_URL as ORIGIN } from "@/lib/seo";
import { toBengaliNumerals } from "@/lib/bdNumerals";

export const Route = createFileRoute("/")({
  head: () => {
    const seo = buildSeo({
      title: "Bangla Calculator — 51+ Free Calculators for BD",
      description:
        "51+ free bilingual (English & বাংলা) calculators for Bangladesh: EMI, Zakat, Land (katha-bigha), Prayer Times, NBR Tax, BMI, Faraid, GPA & more.",
      path: "/",
    });
    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          name: "Bangla Calculator",
          url: ORIGIN,
          potentialAction: {
            "@type": "SearchAction",
            target: `${ORIGIN}/?q={search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        },
        {
          "@type": "Organization",
          name: "Bangla Calculator",
          url: ORIGIN,
        },
        {
          "@type": "ItemList",
          itemListOrder: "https://schema.org/ItemListOrderAscending",
          numberOfItems: 8,
          itemListElement: [
            "age-calculator",
            "scientific-calculator",
            "zakat-calculator",
            "prayer-times",
            "faraid-calculator",
            "bmi-calculator",
            "emi-calculator",
            "land-calculator",
          ].map((slug, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: getCalculator(slug)?.name.en ?? slug,
            url: `${ORIGIN}/${slug}`,
            item: `${ORIGIN}/${slug}`,
          })),
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q_en,
            acceptedAnswer: { "@type": "Answer", text: f.a_en },
          })),
        },
      ],
    };
    return {
      meta: seo.meta,
      links: seo.links,
      scripts: [
        ...(seo.scripts ?? []),
        { type: "application/ld+json", children: JSON.stringify(ld) },
      ],
    };
  },
  component: HomePage,
});

const HERO_PILLS = [
  "zakat-calculator",
  "prayer-times",
  "faraid-calculator",
  "land-calculator",
  "emi-calculator",
  "age-calculator",
];

const FEATURED_BD = ["land-calculator", "emi-calculator", "tax-calculator", "duty-calculator"];
const FEATURED_ISLAMIC = ["zakat-calculator", "prayer-times", "sehri-iftar-time", "faraid-calculator"];
const TOP_POPULAR = [
  "age-calculator",
  "scientific-calculator",
  "zakat-calculator",
  "prayer-times",
  "faraid-calculator",
  "bmi-calculator",
  "emi-calculator",
  "land-calculator",
];

function bn(lang: "en" | "bn", value: string | number) {
  return lang === "bn" ? toBengaliNumerals(value) : String(value);
}

function useDeferMount(delayMs = 0): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const schedule =
      typeof window !== "undefined" && "requestIdleCallback" in window
        ? (cb: () => void) =>
            (window as unknown as { requestIdleCallback: (cb: () => void) => number }).requestIdleCallback(cb)
        : (cb: () => void) => window.setTimeout(cb, delayMs);
    schedule(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [delayMs]);
  return ready;
}

function HomePage() {
  const { lang } = useT();
  const showBelowFold = useDeferMount();

  return (
    <div className="py-6 md:py-12">
      <Hero lang={lang} />
      <FeaturedBangladesh lang={lang} />
      <FeaturedIslamic lang={lang} />
      <FaraidSpotlight lang={lang} />
      <TopPopular lang={lang} />
      {showBelowFold && (
        <>
          <WhySection lang={lang} />
          <AboutSection lang={lang} />
          <CommonCalculations lang={lang} />
          <FaqSection lang={lang} />
        </>
      )}
    </div>
  );
}

/* -------------------- Hero -------------------- */

function Hero({ lang }: { lang: "en" | "bn" }) {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const matches = useMemo<Calculator[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return CALCULATORS.filter(
      (c) =>
        c.name.en.toLowerCase().includes(q) ||
        c.name.bn.toLowerCase().includes(q) ||
        c.slug.includes(q),
    ).slice(0, 8);
  }, [query]);
  const trustItems = [
    { en: "Free forever", bn: "সম্পূর্ণ ফ্রি" },
    { en: "No signup", bn: "সাইনআপ লাগবে না" },
    { en: "Bangladesh-first", bn: "বাংলাদেশের জন্য তৈরি" },
    { en: `${CALCULATORS.length}+ calculators`, bn: `${toBengaliNumerals(CALCULATORS.length)}+ ক্যালকুলেটর` },
    { en: "Islamic + Secular", bn: "ইসলামিক ও সাধারণ" },
  ];
  return (
    <section className="mb-10 rounded-2xl border border-border bg-gradient-to-br from-primary via-primary to-primary/80 p-5 text-primary-foreground shadow-xl sm:mb-14 sm:rounded-3xl sm:p-6 md:p-12">
      <h1 className="text-3xl font-bold leading-tight md:text-5xl">
        Every calculator, built for Bangladesh
      </h1>
      <p className="font-bn mt-2 text-2xl font-bold leading-tight text-primary-foreground/90 md:text-4xl">
        বাংলাদেশের জন্য তৈরি সকল ক্যালকুলেটর
      </p>
      <p className="mt-4 max-w-3xl text-sm text-primary-foreground/85 md:text-base">
        {CALCULATORS.length}+ free calculators — from EMI to Zakat, Land to Prayer Times, BMI to Faraid (money &amp; land). All in Bengali and English.
      </p>
      <p className="font-bn mt-1 max-w-3xl text-sm text-primary-foreground/75 md:text-base">
        {toBengaliNumerals(CALCULATORS.length)}+ ফ্রি ক্যালকুলেটর — EMI থেকে যাকাত, জমির হিসাব থেকে নামাজের সময়, BMI থেকে ফরায়েজ (টাকা ও জমি)। সব বাংলা ও ইংরেজিতে।
      </p>

      {/* Search */}
      <div className="relative mt-6 max-w-2xl">
        <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder="Search 51+ calculators... / ৫১+ ক্যালকুলেটর খুঁজুন..."
          aria-label="Search calculators"
          className="w-full rounded-2xl border border-border bg-background py-4 pl-12 pr-4 text-base text-foreground shadow-lg outline-none focus:ring-2 focus:ring-accent"
        />
        {focused && matches.length > 0 && (
          <div className="absolute z-30 mt-2 w-full overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl">
            {matches.map((c) => (
              <Link
                key={c.slug}
                to="/$slug"
                params={{ slug: c.slug }}
                className="flex items-start gap-3 border-b border-border px-4 py-3 last:border-b-0 hover:bg-muted"
              >
                <span className="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-accent/10 text-accent">
                  <Icon name={c.icon} size={16} />
                </span>
                <span className="min-w-0">
                  <span className={`block truncate text-sm font-semibold ${lang === "bn" ? "font-bn" : ""}`}>
                    {c.name[lang]}
                  </span>
                  <span className={`block truncate text-xs text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
                    {c.description[lang]}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Featured pills */}
      <div className="mt-5 flex flex-wrap gap-2">
        {HERO_PILLS.map((slug) => {
          const c = getCalculator(slug);
          if (!c) return null;
          const isFaraid = slug === "faraid-calculator";
          return (
            <Link
              key={slug}
              to="/$slug"
              params={{ slug }}
              className={`group inline-flex min-h-[40px] items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium transition ${
                isFaraid
                  ? "bg-accent text-accent-foreground hover:bg-accent/90"
                  : "bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20"
              }`}
            >
              <Icon name={c.icon} size={13} />
              <span className={lang === "bn" ? "font-bn" : ""}>{c.name[lang]}</span>
              {isFaraid && <span className="ml-1 hidden sm:inline">· Money + Land</span>}
            </Link>
          );
        })}
      </div>

      {/* Trust signals */}
      <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-primary-foreground/80">
        {trustItems.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1.5">
            <Check size={14} className="text-accent" />
            <span>{it.en}</span>
            <span className="font-bn opacity-70">/ {it.bn}</span>
          </span>
        ))}
      </div>
    </section>
  );
}

/* -------------------- Card components -------------------- */

function CalcCard({
  slug,
  lang,
  badges,
  highlight,
  customDescEn,
  customDescBn,
}: {
  slug: string;
  lang: "en" | "bn";
  badges?: string[];
  highlight?: boolean;
  customDescEn?: string;
  customDescBn?: string;
}) {
  const c = getCalculator(slug);
  if (!c) return null;
  const desc = lang === "en" ? customDescEn ?? c.description.en : customDescBn ?? c.description.bn;
  return (
    <Link
      to="/$slug"
      params={{ slug }}
      className={`group relative flex flex-col rounded-2xl border bg-card p-5 transition hover:-translate-y-0.5 hover:shadow-lg ${
        highlight
          ? "border-accent shadow-md ring-1 ring-accent/30"
          : "border-border hover:border-accent"
      }`}
    >
      {highlight && (
        <span className="absolute -top-2 left-5 inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-accent-foreground">
          <Sparkles size={10} /> BD-Exclusive
        </span>
      )}
      <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent">
        <Icon name={c.icon} size={22} />
      </div>
      <h3 className={`text-base font-semibold leading-tight ${lang === "bn" ? "font-bn" : ""}`}>
        {c.name[lang]}
      </h3>
      <p className={`mt-1 line-clamp-2 text-xs text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
        {desc}
      </p>
      {badges && badges.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {badges.map((b) => (
            <span
              key={b}
              className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground"
            >
              {b}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}

function SectionHeader({
  emoji,
  title,
  subtitle,
}: {
  emoji: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2">
        <span className="text-2xl">{emoji}</span>
        <h2 className="text-xl font-bold tracking-tight md:text-2xl">{title}</h2>
      </div>
      {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
    </div>
  );
}

/* -------------------- Section 3a: Built for Bangladesh -------------------- */

function FeaturedBangladesh({ lang }: { lang: "en" | "bn" }) {
  const cards: { slug: string; badges: string[]; descEn?: string; descBn?: string }[] = [
    {
      slug: "land-calculator",
      badges: ["BD-Exclusive"],
      descEn: "Convert katha, bigha, shotok, decimal — full BD land units",
      descBn: "কাঠা, বিঘা, শতক, ডেসিমেল রূপান্তর",
    },
    {
      slug: "emi-calculator",
      badges: ["BD Banks"],
      descEn: "BD bank rates: DBBL, BRAC, City Bank presets",
      descBn: "ডিবিবিএল, ব্র্যাক, সিটি ব্যাংকের EMI প্রিসেট",
    },
    {
      slug: "tax-calculator",
      badges: ["NBR FY 2025-26"],
      descEn: "FY 2025-26 NBR tax slabs",
      descBn: "অর্থবছর ২০২৫-২৬ NBR ট্যাক্স স্ল্যাব",
    },
    {
      slug: "duty-calculator",
      badges: ["BD Customs"],
      descEn: "Full BD duty stack: CD, SD, VAT, AIT, RD",
      descBn: "সম্পূর্ণ BD শুল্ক: CD, SD, VAT, AIT, RD",
    },
  ];
  return (
    <section className="mb-10 sm:mb-14">
      <SectionHeader
        emoji="🇧🇩"
        title="Built for Bangladesh"
        subtitle="BD-specific calculators no other site does properly"
      />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((card, i) => (
          <CalcCard
            key={i}
            slug={card.slug}
            lang={lang}
            badges={card.badges}
            customDescEn={card.descEn}
            customDescBn={card.descBn}
          />
        ))}
      </div>
    </section>
  );
}

/* -------------------- Section 3b: Islamic -------------------- */

function FeaturedIslamic({ lang }: { lang: "en" | "bn" }) {
  const cards: { slug: string; badges: string[]; highlight?: boolean; descEn?: string; descBn?: string }[] = [
    {
      slug: "zakat-calculator",
      badges: ["Hanafi"],
      descEn: "Zakat with vori, BDT, BD assets",
      descBn: "যাকাত হিসাব করুন vori, BDT, DPS-FDR সহ",
    },
    {
      slug: "prayer-times",
      badges: ["All 64 Districts"],
      descEn: "Prayer times for all 64 BD districts",
      descBn: "৬৪ জেলার নামাজের সময়সূচি",
    },
    {
      slug: "sehri-iftar-time",
      badges: ["Ramadan-ready"],
      descEn: "Live Iftar countdown, full Ramadan calendar",
      descBn: "রমাদানের সেহরি ইফতার সময়",
    },
    {
      slug: "faraid-calculator",
      badges: ["Money + Land", "BD-Exclusive"],
      highlight: true,
      descEn: "Distribute cash AND land per Quranic shares",
      descBn: "ফরায়েজ অনুযায়ী টাকা ও জমি বণ্টন",
    },
  ];
  return (
    <section className="mb-10 sm:mb-14">
      <SectionHeader
        emoji="🕌"
        title="Islamic Calculators"
        subtitle="ইসলামিক ক্যালকুলেটর / Built with Hanafi madhhab default for Bangladesh"
      />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((card, i) => (
          <CalcCard
            key={i}
            slug={card.slug}
            lang={lang}
            badges={card.badges}
            highlight={card.highlight}
            customDescEn={card.descEn}
            customDescBn={card.descBn}
          />
        ))}
      </div>
    </section>
  );
}

/* -------------------- Section 4: Faraid Spotlight -------------------- */

function FaraidSpotlight({ lang }: { lang: "en" | "bn" }) {
  const rows = [
    { heir_en: "Wife", heir_bn: "স্ত্রী", share: "1/8", money: "1.25", land: "0.625" },
    { heir_en: "Son", heir_bn: "পুত্র", share: "7/16", money: "4.37", land: "2.19" },
    { heir_en: "Daughter 1", heir_bn: "কন্যা ১", share: "7/32", money: "2.18", land: "1.09" },
    { heir_en: "Daughter 2", heir_bn: "কন্যা ২", share: "7/32", money: "2.18", land: "1.09" },
  ];
  return (
    <section className="mb-10 overflow-hidden sm:mb-14 rounded-2xl border border-accent/30 bg-gradient-to-br from-primary/5 via-background to-accent/5 p-4 sm:rounded-3xl sm:p-6 md:p-10">
      <div className="grid gap-6 lg:grid-cols-2 lg:gap-12">
        {/* Left */}
        <div className="min-w-0">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-accent sm:text-xs">
            <Sparkles size={12} /> BD-Exclusive Spotlight
          </span>
          <h2 className="mt-3 text-xl font-bold leading-tight break-words sm:text-2xl md:text-3xl">
            🎯 The only Faraid calculator in Bangladesh that handles both money and land
          </h2>
          <h3 className="font-bn mt-2 text-base leading-snug text-muted-foreground break-words [overflow-wrap:anywhere] sm:text-lg md:text-xl">
            বাংলাদেশের একমাত্র টাকা + জমি সহ ফরায়েজ ক্যালকুলেটর
          </h3>
          <p className="mt-3 text-sm text-foreground/85 md:text-base">
            70% of inheritance disputes in Bangladesh involve land — not just cash. We handle both, per Quranic Faraid rules.
          </p>
          <p className="font-bn mt-2 text-sm text-muted-foreground md:text-base">
            বাংলাদেশে ৭০% উত্তরাধিকার বিরোধ জমি নিয়ে — শুধু টাকা নয়। আমরা দুটোই হ্যান্ডেল করি, কুরআনিক ফরায়েজ অনুযায়ী।
          </p>

          <ul className="mt-4 space-y-2 text-sm">
            {[
              ["Add unlimited land plots (katha, bigha, shotok, decimal)", "একাধিক জমি প্লট যোগ করুন (কাঠা, বিঘা, শতক, ডেসিমেল)"],
              ["Money and land distributed together — same fractions", "টাকা ও জমি একসাথে বণ্টন — একই ভগ্নাংশে"],
              ["Smart guidance when land can't be physically divided", "জমি বিভাজন সম্ভব না হলে বিকল্প পরামর্শ"],
              ["Full guide: mutation (নামজারি), warish sanad, partition process", "নামজারি, ওয়ারিশান সনদ — সম্পূর্ণ আইনি প্রক্রিয়ার গাইড"],
              ["Hanafi madhhab default with Quranic references", "হানাফী মাযহাব ডিফল্ট, কুরআনিক রেফারেন্স সহ"],
              ["PDF export — share with family", "PDF এক্সপোর্ট — পরিবারের সাথে শেয়ার করুন"],
            ].map(([en, bn], i) => (
              <li key={i} className="flex gap-2">
                <Check size={16} className="mt-0.5 flex-shrink-0 text-accent" />
                <span className="min-w-0 break-words">
                  <span>{en}</span>
                  <span className="font-bn ml-1 text-muted-foreground">/ {bn}</span>
                </span>
              </li>
            ))}
          </ul>

          <Link
            to="/$slug"
            params={{ slug: "faraid-calculator" }}
            className="mt-5 flex w-full flex-col items-center justify-center gap-0.5 rounded-xl bg-accent px-4 py-3 text-center font-bold text-accent-foreground shadow-md transition hover:bg-accent/90 sm:inline-flex sm:w-auto sm:flex-row sm:gap-2"
          >
            <span className="inline-flex items-center gap-2 text-sm">
              Try Faraid Calculator
              <ArrowRight size={16} />
            </span>
            <span className="font-bn text-xs opacity-90 sm:text-sm">ফরায়েজ ক্যালকুলেটর চালু করুন</span>
          </Link>
        </div>

        {/* Right */}
        <div className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Example <span className="font-bn">/ উদাহরণ</span>
          </p>
          <p className="mt-2 text-sm font-medium">Family: Wife + 1 Son + 2 Daughters</p>
          <p className="font-bn text-sm text-muted-foreground">পরিবার: স্ত্রী + ১ পুত্র + ২ কন্যা</p>
          <p className="mt-2 text-sm font-medium">
            Estate: <span className="tabular-nums">৳10,00,000</span> + 5 katha land
          </p>
          <p className="font-bn text-sm text-muted-foreground">
            সম্পদ: ৳১০,০০,০০০ + ৫ কাঠা জমি (ময়মনসিংহ)
          </p>

          <div className="mt-4 -mx-4 overflow-x-auto px-4 sm:-mx-5 sm:px-5">
            <table className="w-full text-xs sm:min-w-[420px] sm:text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] uppercase tracking-wide text-muted-foreground sm:text-xs sm:normal-case sm:tracking-normal">
                  <th className="py-2 pr-2 font-medium sm:pr-3">Heir</th>
                  <th className="py-2 pr-2 font-medium sm:pr-3">Share</th>
                  <th className="py-2 pr-2 text-right font-medium sm:pr-3">
                    <span className="hidden sm:inline">Money (৳ Lakh)</span>
                    <span className="sm:hidden">৳ Lakh</span>
                  </th>
                  <th className="py-2 text-right font-medium">
                    <span className="hidden sm:inline">Land (Katha)</span>
                    <span className="sm:hidden">Katha</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-border/50 last:border-b-0">
                    <td className="py-2 pr-2 sm:pr-3">
                      <span className={lang === "bn" ? "font-bn" : ""}>
                        {lang === "bn" ? r.heir_bn : r.heir_en}
                      </span>
                    </td>
                    <td className="py-2 pr-2 font-mono text-[11px] tabular-nums sm:pr-3 sm:text-xs">{r.share}</td>
                    <td className="py-2 pr-2 text-right tabular-nums sm:pr-3">{bn(lang, r.money)}</td>
                    <td className="py-2 text-right tabular-nums">{bn(lang, r.land)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
            📜 Per Qur'an (An-Nisa 4:11, 4:12){" "}
            <span className="font-bn">/ কুরআন (আন-নিসা ৪:১১, ৪:১২) অনুসারে</span>
          </p>
          <Link
            to="/$slug"
            params={{ slug: "faraid-calculator" }}
            className="mt-3 inline-flex min-h-[44px] items-center gap-1 text-sm font-semibold text-accent hover:underline"
          >
            See Full Calculation <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Section 5: Top Popular -------------------- */

function TopPopular({ lang }: { lang: "en" | "bn" }) {
  return (
    <section className="mb-10 sm:mb-14">
      <SectionHeader emoji="🔥" title="Most Popular / সবচেয়ে জনপ্রিয়" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {TOP_POPULAR.map((slug) => (
          <CalcCard
            key={slug}
            slug={slug}
            lang={lang}
            badges={slug === "faraid-calculator" ? ["Money + Land"] : undefined}
            highlight={slug === "faraid-calculator"}
          />
        ))}
      </div>
    </section>
  );
}

/* -------------------- Section 6: Browse by Category (removed — duplicates sidebar) -------------------- */


/* -------------------- Section 7: Why -------------------- */

function WhySection({ lang }: { lang: "en" | "bn" }) {
  const items = [
    {
      icon: MapPin,
      title_en: "Bangladesh-First",
      title_bn: "বাংলাদেশ-ভিত্তিক",
      desc_en:
        "Calculators built with BD context: katha-bigha, NBR tax slabs, BD bank rates, vori, Bengali calendar, Karachi prayer method, Hanafi madhhab. Plus the only Faraid calculator that handles money AND land.",
      desc_bn:
        "BD প্রেক্ষাপটে: কাঠা-বিঘা, NBR স্ল্যাব, BD ব্যাংক হার, ভরি, বঙ্গাব্দ, করাচী মেথড, হানাফী মাযহাব। সাথে একমাত্র টাকা ও জমি সহ ফরায়েজ ক্যালকুলেটর।",
    },
    {
      icon: Heart,
      title_en: "Free Forever",
      title_bn: "চিরকাল ফ্রি",
      desc_en:
        "Every calculator is 100% free. No signup, no paywall. Never ads in calculator results, prayer times, Quranic content, or Faraid distributions.",
      desc_bn:
        "প্রতিটি ক্যালকুলেটর ১০০% ফ্রি। সাইনআপ নেই, পেওয়াল নেই। ক্যালকুলেটর ফলাফল, নামাজ, কুরআন বা ফরায়েজে কখনো বিজ্ঞাপন নয়।",
    },
    {
      icon: Globe,
      title_en: "Bilingual",
      title_bn: "দ্বিভাষিক",
      desc_en:
        "Full English ↔ বাংলা toggle. Bengali numerals (০-৯), Bengali & Hijri calendars, three-way conversion. Faraid includes Bengali land terms (কাঠা, বিঘা, নামজারি, ওয়ারিশান সনদ).",
      desc_bn:
        "ইংরেজি ↔ বাংলা টগল। বাংলা সংখ্যা (০-৯), বঙ্গাব্দ ও হিজরি ক্যালেন্ডার। ফরায়েজে বাংলা জমির পরিভাষা (কাঠা, বিঘা, নামজারি, ওয়ারিশান সনদ)।",
    },
    {
      icon: Zap,
      title_en: "Fast & Private",
      title_bn: "দ্রুত ও প্রাইভেট",
      desc_en:
        "Everything runs in your browser. No data sent to servers. Works offline once loaded. Your Zakat, Faraid, and family wealth never leave your device.",
      desc_bn:
        "সব ব্রাউজারেই চলে। সার্ভারে কোনো ডেটা যায় না। অফলাইনেও কাজ করে। যাকাত, ফরায়েজ বা পারিবারিক সম্পদ ডিভাইসেই থাকে।",
    },
  ];
  return (
    <section className="mb-10 rounded-2xl border border-border bg-muted/40 p-5 sm:mb-14 sm:rounded-3xl sm:p-6 md:p-10">
      <h2 className="mb-8 text-xl font-bold tracking-tight md:text-2xl">
        Why Bangla Calculator
      </h2>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((item, i) => (
          <div key={i}>
            <span className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent/15 text-accent">
              <item.icon size={22} />
            </span>
            <h3 className={`text-base font-bold ${lang === "bn" ? "font-bn" : ""}`}>
              {lang === "bn" ? item.title_bn : item.title_en}
            </h3>
            <p className={`mt-1.5 text-xs leading-relaxed text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
              {lang === "bn" ? item.desc_bn : item.desc_en}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------- Section 8: About -------------------- */

function AboutSection({ lang }: { lang: "en" | "bn" }) {
  return (
    <section className="mb-10 grid gap-6 rounded-2xl border border-border bg-card p-5 sm:mb-14 sm:rounded-3xl sm:p-6 md:grid-cols-2 md:p-10">
      <div>
        <h2 className="text-xl font-bold tracking-tight md:text-2xl">About Bangla Calculator</h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground/85">
          Bangla Calculator is built by Bangladeshi developers for Bangladeshi users. Whether you're an SSC student calculating GPA, a businessperson computing customs duty, a homeowner converting katha to square feet, a Muslim calculating Zakat or checking prayer times, or a family <strong>distributing inherited property (cash + land) per Faraid rules</strong> — we've built the tools for you. {CALCULATORS.length}+ calculators, all free, all in one place, in Bengali and English.
        </p>
      </div>
      <div>
        <h2 className="font-bn text-xl font-bold tracking-tight md:text-2xl">আমাদের সম্পর্কে</h2>
        <p className="font-bn mt-3 text-sm leading-relaxed text-foreground/85">
          বাংলা ক্যালকুলেটর বাংলাদেশী ডেভেলপারদের তৈরি, বাংলাদেশী মানুষদের জন্য। আপনি SSC শিক্ষার্থী হোন GPA গণনা করছেন, ব্যবসায়ী হোন কাস্টমস ডিউটি হিসাব করছেন, গৃহকর্তা হোন কাঠা থেকে বর্গফুটে রূপান্তর করছেন, মুসলিম হোন যাকাত হিসাব বা নামাজের সময় দেখছেন, কিংবা পরিবার হোন <strong>উত্তরাধিকার সম্পত্তি (টাকা + জমি) ফরায়েজ অনুযায়ী বণ্টন করছেন</strong> — আমরা সবার জন্য টুল তৈরি করেছি। {toBengaliNumerals(CALCULATORS.length)}+ ক্যালকুলেটর, সবগুলো ফ্রি, এক জায়গায়, বাংলা ও ইংরেজিতে।
        </p>
      </div>
    </section>
  );
}

/* -------------------- Section 9: Common calculations -------------------- */

const COMMON_GROUPS: { title: string; links: { label_en: string; label_bn: string; slug: string }[] }[] = [
  {
    title: "Daily life",
    links: [
      { label_en: "Calculate age from date of birth", label_bn: "জন্ম তারিখ থেকে বয়স", slug: "age-calculator" },
      { label_en: "Today's prayer times in Dhaka", label_bn: "আজকের নামাজের সময়", slug: "prayer-times" },
      { label_en: "Today's Hijri date", label_bn: "আজকের হিজরি তারিখ", slug: "hijri-date-converter" },
      { label_en: "Sehri Iftar time today", label_bn: "আজকের সেহরি ইফতার", slug: "sehri-iftar-time" },
    ],
  },
  {
    title: "Money & finance",
    links: [
      { label_en: "Calculate Zakat on gold", label_bn: "সোনার যাকাত হিসাব", slug: "zakat-calculator" },
      { label_en: "EMI on ৳5 lakh home loan", label_bn: "৫ লাখ টাকার হোম লোনের EMI", slug: "emi-calculator" },
      { label_en: "BD income tax for ৳10 lakh salary", label_bn: "১০ লাখ বেতনের ট্যাক্স", slug: "tax-calculator" },
      { label_en: "Mobile phone import duty Bangladesh", label_bn: "মোবাইল ইম্পোর্ট ডিউটি", slug: "duty-calculator" },
      { label_en: "DPS maturity calculator", label_bn: "DPS মেচুরিটি", slug: "compound-interest-calculator" },
    ],
  },
  {
    title: "Land & inheritance",
    links: [
      { label_en: "1 katha to square feet", label_bn: "১ কাঠা = কত বর্গফুট", slug: "land-calculator" },
      { label_en: "Faraid distribution for 5 katha land", label_bn: "৫ কাঠা জমির ফরায়েজ বণ্টন", slug: "faraid-calculator" },
      { label_en: "How to divide inherited property in Bangladesh", label_bn: "জমি বণ্টন বাংলাদেশ", slug: "faraid-calculator" },
      { label_en: "Wife and 2 son 1 daughter property share", label_bn: "স্ত্রী, ২ পুত্র, ১ কন্যার সম্পত্তি বণ্টন", slug: "faraid-calculator" },
      { label_en: "Daughter's share in Islamic inheritance", label_bn: "কন্যার ফরায়েজ অংশ", slug: "faraid-calculator" },
      { label_en: "Inheritance with land and money", label_bn: "জমি ও টাকার মিশ্র বণ্টন", slug: "faraid-calculator" },
      { label_en: "Mutation namjari procedure Bangladesh", label_bn: "নামজারি প্রক্রিয়া", slug: "faraid-calculator" },
      { label_en: "Warish sanad Bangladesh", label_bn: "ওয়ারিশান সনদ", slug: "faraid-calculator" },
    ],
  },
  {
    title: "Health",
    links: [
      { label_en: "BMI for South Asian women", label_bn: "এশিয়ান নারীর BMI", slug: "bmi-calculator" },
      { label_en: "Daily calorie needs", label_bn: "দৈনিক ক্যালরির চাহিদা", slug: "calorie-calculator" },
    ],
  },
  {
    title: "Education",
    links: [
      { label_en: "GPA calculation for SSC", label_bn: "SSC GPA হিসাব", slug: "gpa-calculator" },
      { label_en: "Casio FX-991ES Plus online", label_bn: "অনলাইন ক্যাসিও", slug: "casio-fx-991es" },
      { label_en: "Quadratic formula solver", label_bn: "কোয়াড্রাটিক সমাধান", slug: "quadratic-formula-solver" },
    ],
  },
  {
    title: "Conversion",
    links: [
      { label_en: "165 cm to feet", label_bn: "১৬৫ সেমি = কত ফুট", slug: "cm-to-feet" },
      { label_en: "1 mon to kg", label_bn: "১ মন = কত কেজি", slug: "kg-to-pounds" },
      { label_en: "Qibla direction from Dhaka", label_bn: "ঢাকা থেকে কিবলা", slug: "qibla-direction" },
      { label_en: "Bengali to English calendar", label_bn: "বাংলা থেকে ইংরেজি ক্যালেন্ডার", slug: "date-calculator" },
    ],
  },
];

function CommonCalculations({ lang }: { lang: "en" | "bn" }) {
  return (
    <section className="mb-10 sm:mb-14">
      <SectionHeader emoji="🔎" title="Common calculations / প্রায়শই ব্যবহৃত হিসাব" />
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {COMMON_GROUPS.map((g) => (
          <div key={g.title} className="rounded-2xl border border-border bg-card p-5">
            <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-muted-foreground">
              {g.title}
            </h3>
            <ul className="space-y-2">
              {g.links.map((l, i) => (
                <li key={i}>
                  <Link
                    to="/$slug"
                    params={{ slug: l.slug }}
                    className={`group inline-flex min-h-[36px] items-center gap-1 py-1 text-sm text-foreground/85 hover:text-accent ${
                      lang === "bn" ? "font-bn" : ""
                    }`}
                  >
                    <span>{lang === "bn" ? l.label_bn : l.label_en}</span>
                    <ArrowRight size={12} className="opacity-0 transition group-hover:opacity-100" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------- FAQs -------------------- */

const FAQS: { q_en: string; q_bn: string; a_en: string; a_bn: string }[] = [
  {
    q_en: "What is Bangla Calculator?",
    q_bn: "বাংলা ক্যালকুলেটর কী?",
    a_en: `A free suite of ${CALCULATORS.length}+ calculators built specifically for Bangladesh — covering finance, Islamic, health, math, conversions and BD-specific tools like land (katha/bigha), NBR tax, and Faraid for both money and land.`,
    a_bn: `বাংলাদেশের জন্য তৈরি ${toBengaliNumerals(CALCULATORS.length)}+ ফ্রি ক্যালকুলেটরের সমষ্টি — ফাইন্যান্স, ইসলামিক, স্বাস্থ্য, গণিত, কনভার্টার এবং BD-নির্দিষ্ট টুল (জমি, NBR ট্যাক্স, টাকা ও জমি সহ ফরায়েজ)।`,
  },
  {
    q_en: "Is Bangla Calculator free?",
    q_bn: "বাংলা ক্যালকুলেটর কি ফ্রি?",
    a_en: "Yes — every calculator is 100% free with no signup or paywall.",
    a_bn: "হ্যাঁ — প্রতিটি ক্যালকুলেটর ১০০% ফ্রি, কোনো সাইনআপ বা পেমেন্ট লাগে না।",
  },
  {
    q_en: "Do I need to sign up?",
    q_bn: "সাইন আপ করতে হবে?",
    a_en: "No. There are no accounts, no email collection, and no tracking pixels.",
    a_bn: "না। কোনো অ্যাকাউন্ট, ইমেইল বা ট্র্যাকিং নেই।",
  },
  {
    q_en: "How is this different from Google Calculator?",
    q_bn: "Google ক্যালকুলেটর থেকে আলাদা কীভাবে?",
    a_en: "We focus on Bangladesh: katha-bigha, NBR tax slabs, BD bank EMI, vori-based Zakat, 64-district prayer times, and Faraid for both money and land. Google doesn't.",
    a_bn: "আমরা বাংলাদেশ-কেন্দ্রিক: কাঠা-বিঘা, NBR স্ল্যাব, BD ব্যাংকের EMI, ভরি ভিত্তিক যাকাত, ৬৪ জেলার নামাজের সময়, টাকা ও জমি সহ ফরায়েজ।",
  },
  {
    q_en: "Can I use these calculators offline?",
    q_bn: "অফলাইনে ব্যবহার করা যায়?",
    a_en: "Most calculators run entirely in your browser, so they work offline once the page loads.",
    a_bn: "বেশিরভাগ ক্যালকুলেটর ব্রাউজারেই চলে — পেজ লোড হলে অফলাইনেও কাজ করে।",
  },
  {
    q_en: "Are calculations accurate?",
    q_bn: "হিসাব কতটা সঠিক?",
    a_en: "Formulas follow standard methods (NBR slabs, scholarly Faraid, IMF prayer-time methods). For final legal, tax, or religious decisions, consult a qualified expert.",
    a_bn: "সূত্র মানসম্মত (NBR স্ল্যাব, ফরায়েজ, প্রার্থনা পদ্ধতি)। চূড়ান্ত আইনি, কর বা ধর্মীয় সিদ্ধান্তে যোগ্য বিশেষজ্ঞের পরামর্শ নিন।",
  },
  {
    q_en: "Are the Islamic calculators religiously accurate?",
    q_bn: "ইসলামিক ক্যালকুলেটরগুলো কি ধর্মীয়ভাবে সঠিক?",
    a_en: "Yes — Hanafi madhhab default (most common in Bangladesh), Karachi method for prayer times, classical Faraid rules with Quranic citations.",
    a_bn: "হ্যাঁ — হানাফী মাযহাব ডিফল্ট, করাচী মেথড, ক্লাসিক্যাল ফরায়েজ নিয়ম ও কুরআনিক রেফারেন্স।",
  },
  {
    q_en: "Which madhhab is the Zakat / Faraid calculator based on?",
    q_bn: "যাকাত / ফরায়েজ ক্যালকুলেটর কোন মাযহাব অনুসরণ করে?",
    a_en: "Hanafi by default, with options where applicable.",
    a_bn: "ডিফল্টভাবে হানাফী, প্রযোজ্য ক্ষেত্রে অপশন থাকে।",
  },
  {
    q_en: "Can the Faraid Calculator handle land inheritance?",
    q_bn: "ফরায়েজ ক্যালকুলেটর কি জমি বণ্টন করতে পারে?",
    a_en: "Yes — it's the only one in Bangladesh that handles BOTH money AND land. Add multiple plots in katha, bigha, shotok or decimal, and each heir's share is shown as both fraction and physical area.",
    a_bn: "হ্যাঁ — এটি বাংলাদেশের একমাত্র টুল যা টাকা ও জমি উভয়ই বণ্টন করে। কাঠা, বিঘা, শতক, ডেসিমেলে একাধিক প্লট যোগ করুন; প্রতিটি ওয়ারিশের অংশ ভগ্নাংশ ও জমির পরিমাণ দুইভাবেই দেখায়।",
  },
  {
    q_en: "How accurate are the Prayer Times for Bangladesh?",
    q_bn: "বাংলাদেশে নামাজের সময় কতটা সঠিক?",
    a_en: "We use the Karachi method (commonly used in BD) with Hanafi Asr, computed for all 64 districts.",
    a_bn: "করাচী মেথড (BD-তে প্রচলিত), হানাফী আসর — ৬৪ জেলার জন্য হিসাবকৃত।",
  },
  {
    q_en: "Can I use the Casio emulator in exams?",
    q_bn: "পরীক্ষায় ক্যাসিও এমুলেটর ব্যবহার করা যাবে?",
    a_en: "It's for practice and learning. Most exam boards require a physical Casio.",
    a_bn: "এটি অনুশীলন ও শেখার জন্য। বেশিরভাগ পরীক্ষায় ফিজিক্যাল ক্যাসিও দরকার।",
  },
  {
    q_en: "Is my data private?",
    q_bn: "আমার ডেটা কি প্রাইভেট?",
    a_en: "Yes — calculations run in your browser. Inputs are never sent to a server.",
    a_bn: "হ্যাঁ — হিসাব ব্রাউজারেই চলে। ইনপুট সার্ভারে যায় না।",
  },
];

function FaqSection({ lang }: { lang: "en" | "bn" }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="mb-8">
      <SectionHeader emoji="❓" title="Frequently asked questions / প্রশ্নোত্তর" />
      <div className="space-y-2">
        {FAQS.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={i} className="overflow-hidden rounded-xl border border-border bg-card">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                aria-expanded={isOpen}
                className="flex min-h-[48px] w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition hover:bg-muted/50"
              >
                <span className={`text-sm font-semibold ${lang === "bn" ? "font-bn" : ""}`}>
                  {lang === "bn" ? f.q_bn : f.q_en}
                </span>
                <ChevronDown
                  size={18}
                  className={`flex-shrink-0 text-muted-foreground transition ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
              {isOpen && (
                <div className={`px-4 pb-4 text-sm leading-relaxed text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
                  {lang === "bn" ? f.a_bn : f.a_en}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
